import type { ReactNode } from "react";
import { useMemo } from "react";

import {
  getActiveSubItemRoute,
  getActiveNAFClientId,
} from "../sidebar.highlighting";
import usePathname from "../../../../components/hooks/usePathname";

import SidebarAOIOBItem from "./SidebarAOIOBItem";
import SidebarNAFItem from "./SidebarNAFItem";
import SidebarStandardItem from "./SidebarStandardItem";

import type { SidebarOptionItem, NAFOption } from "../sidebar.types";

interface UseMenuItemOptionsArgs {
  menuOptions: SidebarOptionItem[];
  customMenuOptions?: ReactNode;
  isOpen?: boolean;
  itemKey: string;
}

function getKeyFromNAFOption(nafOption: NAFOption, index: number): string {
  switch (nafOption?.discriminator) {
    case "legacy-naf":
      return nafOption.title;
    case "app":
      return nafOption.app.id;
    default:
      return `unknown_naf_option_${index}`;
  }
}

export default function useMenuItemOptions({
  menuOptions,
  customMenuOptions,
  isOpen,
  itemKey,
}: UseMenuItemOptionsArgs): ReactNode {
  const pathname = usePathname();

  return useMemo(() => {
    if (customMenuOptions) {
      return customMenuOptions;
    }

    const activeSubItemRoute = getActiveSubItemRoute(menuOptions, pathname);
    const activeNAFClientId = getActiveNAFClientId(menuOptions, pathname);
    const hideIcon = itemKey === "tools";

    return menuOptions.map((option, index) => {
      if (option?.type === "aoiob") {
        return (
          <SidebarAOIOBItem
            key="aoiob"
            title={option.title}
            isVisible={isOpen}
          />
        );
      }

      if (option?.type === "naf") {
        const nafClientId =
          option.nafOption.discriminator === "app"
            ? option.nafOption.app.client_id
            : undefined;
        return (
          <SidebarNAFItem
            key={`naf_${getKeyFromNAFOption(option.nafOption, index)}`}
            hideIcon={hideIcon}
            nafOption={option.nafOption}
            isVisible={isOpen}
            isActive={
              nafClientId !== undefined && nafClientId === activeNAFClientId
            }
          />
        );
      }

      return (
        <SidebarStandardItem
          key={option.route}
          title={option.title}
          route={option.route}
          icon={option.icon}
          newTab={option.newTab}
          isActive={activeSubItemRoute === option.route}
          isVisible={isOpen}
          hideIcon={hideIcon}
        />
      );
    });
  }, [customMenuOptions, menuOptions, isOpen, itemKey, pathname]);
}
